import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { GetWithdrawList, CreateWithdraw, SubmitKycBank, SubmitKycUpi, GetKycList, GetWalletAmount } from "../../services/api";
import { updateUser } from "../../features/auth/authSlice";

const tabs = ["ALL", "PENDING", "SUCCESS", "REJECTED"];

export default function Withdraw() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state?.auth?.user);
    const [withdraws, setWithdraws] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Pagination states
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    
    const [activeTab, setActiveTab] = useState("ALL");

    // KYC states
    const [kycData, setKycData] = useState(undefined);
    const [showKycOptions, setShowKycOptions] = useState(false);
    const [isKycBankModalOpen, setIsKycBankModalOpen] = useState(false);
    const [isKycUpiModalOpen, setIsKycUpiModalOpen] = useState(false);

    // KYC Info Modal states
    const [isKycInfoModalOpen, setIsKycInfoModalOpen] = useState(false);
    const [kycInfoList, setKycInfoList] = useState([]);
    const [fetchingKycInfo, setFetchingKycInfo] = useState(false);
    
    // Bank KYC Form
    const [bankAccountName, setBankAccountName] = useState("");
    const [bankAccountNumber, setBankAccountNumber] = useState("");
    const [bankIfsc, setBankIfsc] = useState("");
    const [bankName, setBankName] = useState("");

    // UPI KYC Form
    const [upiAccountName, setUpiAccountName] = useState("");
    const [upiId, setUpiId] = useState("");

    useEffect(() => {
        setPage(1);
        fetchWithdraws(1, activeTab);
    }, [activeTab]);

    const fetchKycInfo = async () => {
        setIsKycInfoModalOpen(true);
        setFetchingKycInfo(true);
        try {
            const res = await GetKycList();
            const arr = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
            setKycInfoList(arr.filter(item => item.status === 'approved'));
        } catch (err) {
            console.error("Failed to fetch kyc list", err);
            toast.error("Failed to fetch Kyc Info");
        } finally {
            setFetchingKycInfo(false);
        }
    };

    const fetchWithdraws = async (pageNumber, status = activeTab) => {
        if (pageNumber === 1) setLoading(true);
        else setLoadingMore(true);

        try {
            const res = await GetWithdrawList(pageNumber, 20, status);
            if (res?.data) {
                // Update KYC Status
                const kycObj = res.data.kyc || {};
                const activeKyc = (kycObj.bank && Object.keys(kycObj.bank).length > 0) ? kycObj.bank :
                                  (kycObj.upi && Object.keys(kycObj.upi).length > 0) ? kycObj.upi : null;
                setKycData(activeKyc);
                setShowKycOptions(false);

                if (res?.data?.data) {
                    if (pageNumber === 1) {
                        setWithdraws(res.data.data);
                    } else {
                        setWithdraws(prev => [...prev, ...res.data.data]);
                    }
                    
                    if (res.data?.pagination) {
                        setHasMore(res.data.pagination.hasNext);
                    } else {
                        setHasMore(false);
                    }
                }
            }
        } catch (error) {
            console.error("Failed to fetch withdraws", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        if (!loadingMore && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchWithdraws(nextPage);
        }
    };

    const handleCreateWithdraw = async () => {
        if (!amount || isNaN(amount) || amount <= 0) {
            return;
        }

        if (!Number.isInteger(Number(amount))) {
            toast.error("Withdraw amount must be a whole number");
            return;
        }

        if (Number(amount) > (user?.walletBalance || 0)) {
            toast.error("Insufficient balance");
            return;
        }

        setSubmitting(true);
        try {
            const res = await CreateWithdraw({ amount: Number(amount) });
            if (res) {
                setIsModalOpen(false);
                setAmount("");
                setPage(1);
                fetchWithdraws(1, activeTab);
                GetWalletAmount()
                  .then((res) => {
                    dispatch(updateUser({
                      walletBalance: res.data.walletBalance,
                    }));
                  })
                  .catch((err) => {
                    console.log(err)
                  });
            }
        } catch (error) {
            console.error("Failed to create withdraw", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleKycBankSubmit = async () => {
        if(!bankAccountName || !bankAccountNumber || !bankIfsc || !bankName) {
            toast.error("Please fill all bank details");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                accountName: bankAccountName,
                accountNumber: bankAccountNumber,
                ifsc: bankIfsc,
                bankName: bankName
            };
            await SubmitKycBank(payload);
            toast.success("Bank detail submitted!");
            setIsKycBankModalOpen(false);
            setPage(1);
            fetchWithdraws(1, activeTab);
        } catch (error) {
            console.error("Failed to submit bank kyc", error);
            toast.error("Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleKycUpiSubmit = async () => {
        if(!upiAccountName || !upiId) {
            toast.error("Please fill all UPI details");
            return;
        }

        const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
        if (!upiRegex.test(upiId)) {
            toast.error("Please enter a valid UPI ID (e.g., username@bank)");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                accountName: upiAccountName,
                upiId: upiId
            };
            await SubmitKycUpi(payload);
            toast.success("UPI detail submitted!");
            setIsKycUpiModalOpen(false);
            setPage(1);
            fetchWithdraws(1, activeTab);
        } catch (error) {
            console.error("Failed to submit upi kyc", error);
            toast.error("Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading && kycData === undefined) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center pt-[50px] pb-20 mt-16 text-gray-400">
                Loading...
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white pt-[50px] pb-20 mt-16">
            <div className="max-w-md mx-auto px-4">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-white">Withdraw</h2>
                        <div className="flex items-center gap-1 mt-1 text-gray-400 text-sm">
                            Balance:
                            <img src="./images/goldcoin.png" alt="coin" className="w-5 h-5 rounded-full object-cover" />
                            <span className="text-yellow-400 font-semibold">{user?.walletBalance?.toFixed(2) || '0.00'}</span>
                        </div>
                    </div>
                    {kycData?.status === "approved" && (
                        <div className="flex gap-2">
                            <button
                                onClick={fetchKycInfo}
                                className="bg-[#1f1f1f] hover:bg-[#2a2a2a] px-4 py-2 rounded-full text-sm font-medium transition border border-gray-800 text-white"
                            >
                                Account Info
                            </button>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="bg-red-500 hover:bg-red-500 px-4 py-2 rounded-full text-sm font-medium transition"
                            >
                                New Withdraw
                            </button>
                        </div>
                    )}
                </div>

                {(kycData === null || showKycOptions) ? (
                    <div className="mt-10">
                        <div className="mb-8">
                            <h2 className="text-xl font-bold text-white text-center">Complete KYC</h2>
                            <p className="text-gray-400 text-sm mt-3 text-center">You need to submit your payment details to enable withdrawals.</p>
                        </div>

                        <div className="space-y-4">
                            <button 
                                onClick={() => setIsKycBankModalOpen(true)}
                                className="w-full bg-[#1f1f1f] hover:bg-[#2a2a2a] p-5 rounded-2xl border border-gray-800 text-left transition flex items-center justify-between"
                            >
                                <div>
                                    <h3 className="text-lg font-bold text-white">Bank Account</h3>
                                    <p className="text-gray-400 text-sm mt-1">Submit your standard bank details to receive payments directly to your account.</p>
                                </div>
                                <span className="text-red-500 text-3xl ml-3">›</span>
                            </button>

                            <button 
                                onClick={() => setIsKycUpiModalOpen(true)}
                                className="w-full bg-[#1f1f1f] hover:bg-[#2a2a2a] p-5 rounded-2xl border border-gray-800 text-left transition flex items-center justify-between"
                            >
                                <div>
                                    <h3 className="text-lg font-bold text-white">UPI Payment</h3>
                                    <p className="text-gray-400 text-sm mt-1">Submit your UPI ID for instant wallet transfers.</p>
                                </div>
                                <span className="text-red-500 text-3xl ml-3">›</span>
                            </button>
                        </div>
                    </div>
                ) : kycData?.status === "pending" || kycData?.status === "rejected" ? (
                    <div className="mt-10">
                        <div className="mb-8 text-center">
                            <h2 className="text-xl font-bold text-white">
                                {kycData.status === "rejected" ? "KYC Rejected" : "KYC Pending"}
                            </h2>
                            <p className={`text-sm mt-3 ${kycData.status === "rejected" ? "text-red-400" : "text-yellow-400"}`}>
                                {kycData.status === "rejected" 
                                    ? "Your KYC is rejected. Upload it again to withdraw money." 
                                    : "Your KYC is not approved yet. Please wait for admin verification."}
                            </p>
                        </div>

                        <div className="bg-[#1f1f1f] rounded-2xl p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-white mb-4">Submitted Details</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Type</span>
                                    <span className="text-white uppercase">{kycData.type}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Account Name</span>
                                    <span className="text-white">{kycData.accountName}</span>
                                </div>
                                {kycData.type === 'bank' ? (
                                    <>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Account Number</span>
                                            <span className="text-white">{kycData.accountNumber}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Bank Name</span>
                                            <span className="text-white">{kycData.bankName}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">IFSC Code</span>
                                            <span className="text-white uppercase">{kycData.ifsc}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">UPI ID</span>
                                        <span className="text-white">{kycData.accountNumber || kycData.upiId}</span>
                                    </div>
                                )}
                            </div>

                            {kycData.rejectionReason && kycData.status === "rejected" && (
                                <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                                    <p className="text-sm text-red-500"><span className="font-bold">Reason:</span> {kycData.rejectionReason}</p>
                                </div>
                            )}

                            {kycData.status === "rejected" && (
                                <button
                                    onClick={() => setShowKycOptions(true)}
                                    className="w-full mt-6 bg-red-500 text-white font-medium py-3 rounded-full hover:bg-red-500 transition"
                                >
                                    Upload Again
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Tabs */}
                        <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar pb-1">
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => {
                                        setActiveTab(tab);
                                    }}
                                    className={`flex-1 py-2 rounded-full text-sm font-medium whitespace-nowrap px-4
                                            ${activeTab === tab
                                            ? "bg-red-500"
                                            : "bg-[#1f1f1f] text-gray-400"
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className="space-y-3">
                            {loading ? (
                                <div className="text-center text-gray-400 mt-20 p-4">Loading withdraws...</div>
                            ) : withdraws.length === 0 ? (
                                <div className="text-center text-gray-400 mt-20">
                                    <p>No withdraws found</p>
                                </div>
                            ) : (
                                <>
                                    {withdraws.map((w, idx) => (
                                        <div key={w._id || idx} className="bg-[#1f1f1f] rounded-2xl p-4 mb-3 border border-gray-800">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h3 className="text-white font-medium">Withdraw Request</h3>
                                                    <p className="text-gray-400 text-sm mt-1">
                                                        {w.createdAt ? new Date(w.createdAt).toLocaleString('en-US', {
                                                            day: '2-digit', month: 'short', year: 'numeric',
                                                            hour: '2-digit', minute: '2-digit', hour12: true
                                                        }) : ""}
                                                    </p>
                                                </div>
                                                <div className="text-right flex flex-col items-end">
                                                    <div className="flex items-center gap-1">
                                                        <img src="./images/goldcoin.png" alt="coin" className="w-4 h-4 rounded-full object-cover" />
                                                        <p className="text-white font-bold">{w.reqAmount || w.amount}</p>
                                                    </div>
                                                    <p className={`text-xs mt-1 font-medium ${w.status === 'success' || w.status === 'SUCCESS' ? 'text-green-500' : w.status === 'failed' || w.status === 'FAILED' ? 'text-red-500' : 'text-yellow-500'}`}>
                                                        {w.status?.toUpperCase() || 'PENDING'}
                                                    </p>
                                                </div>
                                            </div>
                                            {w.rejectionReason && (w.status === 'rejected' || w.status === 'failed' || w.status === 'REJECTED' || w.status === 'FAILED') && (
                                                <p className="text-red-400 text-xs mt-2 bg-red-400/10 p-2 rounded italic">Reason: {w.rejectionReason}</p>
                                            )}
                                            {w.adminNote && (
                                                <p className="text-gray-400 text-xs mt-2 bg-gray-800 p-2 rounded">Note: {w.adminNote}</p>
                                            )}
                                        </div>
                                    ))}

                                    {hasMore && (
                                        <div className="text-center pt-4 pb-8">
                                            <button
                                                onClick={handleLoadMore}
                                                disabled={loadingMore}
                                                className="bg-[#1f1f1f] hover:bg-[#2a2a2a] text-white px-6 py-2 rounded-full font-medium transition disabled:opacity-50"
                                            >
                                                {loadingMore ? "Loading..." : "Load More"}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Create Withdraw Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1f1f1f] rounded-2xl p-6 w-full max-w-sm border border-gray-800">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-white">Create Withdraw</h3>
                            <div className="flex items-center gap-1 mt-1 text-gray-400 text-sm">
                                Balance:
                                <img src="./images/goldcoin.png" alt="coin" className="w-4 h-4 rounded-full object-cover" />
                                <span className="text-yellow-400 font-semibold">{user?.walletBalance?.toFixed(2) || '0.00'}</span>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-gray-400 text-sm mb-2">Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="Enter amount"
                                className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="flex-1 py-3 rounded-full bg-gray-800 text-white font-medium hover:bg-gray-700 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateWithdraw}
                                disabled={submitting || !amount || Number(amount) > (user?.walletBalance || 0)}
                                className="flex-1 py-3 rounded-full bg-red-500 text-white font-medium hover:bg-red-500 transition disabled:opacity-50"
                            >
                                {submitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* KYC Bank Modal */}
            {isKycBankModalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1f1f1f] rounded-2xl p-6 w-full max-w-sm border border-gray-800 max-h-[90vh] overflow-y-auto no-scrollbar">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Bank Account Details</h3>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Account Name</label>
                                <input
                                    type="text"
                                    maxLength={45}
                                    value={bankAccountName}
                                    onChange={(e) => setBankAccountName(e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 45))}
                                    placeholder="Enter account name"
                                    className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Account Number</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={30}
                                    value={bankAccountNumber}
                                    onChange={(e) => setBankAccountNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 30))}
                                    placeholder="Enter account number"
                                    className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">IFSC Code</label>
                                <input
                                    type="text"
                                    maxLength={11}
                                    value={bankIfsc}
                                    onChange={(e) => setBankIfsc(e.target.value.slice(0, 11).toUpperCase())}
                                    placeholder="Enter IFSC code"
                                    className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 uppercase placeholder-normal-case"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Bank Name</label>
                                <input
                                    type="text"
                                    maxLength={45}
                                    value={bankName}
                                    onChange={(e) => setBankName(e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 45))}
                                    placeholder="Enter bank name"
                                    className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsKycBankModalOpen(false)}
                                className="flex-1 py-3 rounded-full bg-gray-800 text-white font-medium hover:bg-gray-700 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleKycBankSubmit}
                                disabled={submitting}
                                className="flex-1 py-3 rounded-full bg-red-500 text-white font-medium hover:bg-red-500 transition disabled:opacity-50"
                            >
                                {submitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* KYC UPI Modal */}
            {isKycUpiModalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1f1f1f] rounded-2xl p-6 w-full max-w-sm border border-gray-800 max-h-[90vh] overflow-y-auto no-scrollbar">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">UPI Details</h3>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">Account Name</label>
                                <input
                                    type="text"
                                    value={upiAccountName}
                                    onChange={(e) => setUpiAccountName(e.target.value)}
                                    placeholder="Enter account name"
                                    className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm mb-2">UPI ID</label>
                                <input
                                    type="text"
                                    value={upiId}
                                    onChange={(e) => setUpiId(e.target.value)}
                                    placeholder="Enter UPI ID"
                                    className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsKycUpiModalOpen(false)}
                                className="flex-1 py-3 rounded-full bg-gray-800 text-white font-medium hover:bg-gray-700 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleKycUpiSubmit}
                                disabled={submitting}
                                className="flex-1 py-3 rounded-full bg-red-500 text-white font-medium hover:bg-red-500 transition disabled:opacity-50"
                            >
                                {submitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* KYC Info Modal */}
            {isKycInfoModalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1f1f1f] rounded-2xl p-6 w-full max-w-sm border border-gray-800 max-h-[90vh] overflow-y-auto no-scrollbar">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Account Information</h3>
                        </div>

                        {fetchingKycInfo ? (
                            <div className="py-10 text-center text-gray-400">Loading details...</div>
                        ) : kycInfoList && kycInfoList.length > 0 ? (
                            <div className="space-y-4">
                                {kycInfoList.map((info, idx) => (
                                    <div key={info._id || idx} className="bg-black p-4 rounded-xl border border-gray-800">
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-400 text-sm">Type</span>
                                            <span className="text-white text-sm uppercase font-bold">{info.type}</span>
                                        </div>
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-400 text-sm">Account Name</span>
                                            <span className="text-white text-sm">{info.accountName}</span>
                                        </div>
                                        
                                        {info.type === 'bank' ? (
                                            <>
                                                <div className="flex justify-between mb-2">
                                                    <span className="text-gray-400 text-sm">Account Number</span>
                                                    <span className="text-white text-sm">{info.accountNumber}</span>
                                                </div>
                                                <div className="flex justify-between mb-2">
                                                    <span className="text-gray-400 text-sm">Bank Name</span>
                                                    <span className="text-white text-sm">{info.bankName}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-400 text-sm">IFSC Code</span>
                                                    <span className="text-white text-sm uppercase">{info.ifsc}</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex justify-between">
                                                <span className="text-gray-400 text-sm">UPI ID</span>
                                                <span className="text-white text-sm">{info.accountNumber || info.upiId}</span>
                                            </div>
                                        )}
                                        
                                        <div className="mt-3 pt-3 border-t border-gray-800 flex justify-between">
                                            <span className="text-gray-400 text-sm">Status</span>
                                            <span className={`text-sm font-bold uppercase ${info.status === 'approved' ? 'text-green-500' : info.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'}`}>
                                                {info.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-10 text-center text-gray-400">No account details found.</div>
                        )}

                        <button
                            onClick={() => setIsKycInfoModalOpen(false)}
                            className="w-full mt-6 py-3 rounded-full bg-gray-800 text-white font-medium hover:bg-gray-700 transition"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
